import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { FilterButton } from '@insights_module/components/FilterButton';
import { CSV_EXPORT_TYPE, useCSVExport } from '@insights_module/hooks/useCSVExport';
import { ExportIcon } from '@icons/ExportIcon';
import { Spacer } from '@components/Spacer';
import { CaretIcon } from '@icons/CaretIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { useFiltersContext } from '@insights_module/context/FiltersContext';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';

const Container = tw.div`relative z-50`;
const MenuCard = tw.div`absolute right-0 w-[190px] p-1 mt-1 border border-gray-1 rounded bg-neutral-background shadow-tertiary`;
const MenuCardItem = tw.div`flex items-center justify-between px-2 py-1.5 text-xs font-medium hover:bg-gray-2 cursor-pointer text-text-and-icon`;

export const ExportSelect = (): JSX.Element => {
  const { isEnabled: isExportSummaryEnabled } = useFeatureFlag(FEATURE_FLAG.EXPORT_SUMMARY_CSV);
  const { t } = useTranslation();
  const { showFiltersMenu } = useFiltersContext();
  const { selectedTableType, selectedShow } = useInsightsContext();

  const { initiateCSVExport: initiateGarmCSVExport, inProgress: garmExportsInProgress } =
    useCSVExport(
      selectedTableType === 'shows' && !selectedShow
        ? CSV_EXPORT_TYPE.GROUPS_GARM
        : CSV_EXPORT_TYPE.AUDIOS_GARM
    );

  const {
    initiateCSVExport: initiateContextualCSVExport,
    inProgress: contextualExportsInProgress,
  } = useCSVExport(
    selectedTableType === 'shows' && !selectedShow
      ? CSV_EXPORT_TYPE.GROUPS_CONTEXTUAL
      : CSV_EXPORT_TYPE.AUDIOS_CONTEXTUAL
  );

  const { initiateCSVExport: initiateSummaryCSVExport, inProgress: summaryExportsInProgress } =
    useCSVExport(
      selectedTableType === 'shows' && !selectedShow
        ? CSV_EXPORT_TYPE.GROUPS_SUMMARY
        : CSV_EXPORT_TYPE.AUDIOS_SUMMARY
    );

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const exportSelectRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(exportSelectRef, () => setIsMenuOpen(false));

  const handleExportGarm = () => {
    initiateGarmCSVExport();
    setIsMenuOpen(false);
  };

  const handleExportContextual = () => {
    initiateContextualCSVExport();
    setIsMenuOpen(false);
  };

  const handleExportSummary = () => {
    initiateSummaryCSVExport();
    setIsMenuOpen(false);
  };

  return (
    <Container ref={exportSelectRef} css={[showFiltersMenu && tw`z-0`]}>
      <FilterButton
        tw='min-w-[113px] border border-gray-1 text-text-and-icon'
        onClick={() => setIsMenuOpen((isMenuOpen) => !isMenuOpen)}
        isLoading={garmExportsInProgress || contextualExportsInProgress || summaryExportsInProgress}
        disabled={garmExportsInProgress || contextualExportsInProgress || summaryExportsInProgress}>
        <ExportIcon tw='fill-text-and-icon w-4 h-4' />
        <Spacer direction='horizontal' size='xs' />
        {t('insights.filters_menu.button.export_csv')}
        <Spacer direction='horizontal' size='xs' />
        <CaretIcon tw='fill-text-and-icon' />
      </FilterButton>

      {isMenuOpen && (
        <MenuCard>
          <MenuCardItem onClick={handleExportGarm}>
            {t('insights.filters_menu.button.export_garm')}
          </MenuCardItem>
          <MenuCardItem onClick={handleExportContextual}>
            {t('insights.filters_menu.button.export_contextual')}
          </MenuCardItem>
          {isExportSummaryEnabled && (
            <MenuCardItem onClick={handleExportSummary}>
              {t('insights.filters_menu.button.export_summary')}
            </MenuCardItem>
          )}
        </MenuCard>
      )}
    </Container>
  );
};
